import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import ButtonV2 from 'components/ButtonV2';
import Toaster from 'components/Toaster';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const ToasterDocs = () => {
  const [successToaster, setSuccessToaster] = React.useState(false);
  const [errorToaster, setErrorToaster] = React.useState(false);
  const [infoToaster, setInfoToaster] = React.useState(false);
  const [actionToaster, setActionToaster] = React.useState(false);
  const [linkToaster, setLinkToaster] = React.useState(false);

  const handleUndo = () => {
    alert('Undo clicked!');
    setActionToaster(false);
  };

  return (
    <div>
      <PageHeader>
        <Title>Toaster</Title>
        <Subtitle>
          Toast notification component for displaying temporary messages, confirmations,
          and alerts. Supports success, error, and info states with optional actions.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Live Examples</SectionTitle>
        <SectionDescription>
          Click the buttons below to see the different toaster variations in action.
        </SectionDescription>

        <ComponentDemo label="Success Toaster" gap="12px" wrap>
          <ButtonV2 onClick={() => setSuccessToaster(true)}>
            Show Success
          </ButtonV2>
        </ComponentDemo>

        <ComponentDemo label="Error Toaster" gap="12px" wrap>
          <ButtonV2 onClick={() => setErrorToaster(true)}>
            Show Error
          </ButtonV2>
        </ComponentDemo>

        <ComponentDemo label="Info Toaster" gap="12px" wrap>
          <ButtonV2 onClick={() => setInfoToaster(true)}>
            Show Info
          </ButtonV2>
        </ComponentDemo>

        <ComponentDemo label="Toaster with Action" gap="12px" wrap>
          <ButtonV2 onClick={() => setActionToaster(true)}>
            Show with Undo Action
          </ButtonV2>
        </ComponentDemo>

        <ComponentDemo label="Toaster with Link" gap="12px" wrap>
          <ButtonV2 onClick={() => setLinkToaster(true)}>
            Show with Link
          </ButtonV2>
        </ComponentDemo>
      </Section>

      {/* Toaster instances */}
      <Toaster
        isOpen={successToaster}
        onClose={() => setSuccessToaster(false)}
        title="Success!"
        message="Your changes have been saved successfully."
        type="success"
        duration={5000}
      />

      <Toaster
        isOpen={errorToaster}
        onClose={() => setErrorToaster(false)}
        title="Error"
        message="Something went wrong. Please try again."
        type="error"
        duration={5000}
      />

      <Toaster
        isOpen={infoToaster}
        onClose={() => setInfoToaster(false)}
        title="New Feature Available"
        message="Check out our new dashboard analytics!"
        type="info"
        duration={5000}
      />

      <Toaster
        isOpen={actionToaster}
        onClose={() => setActionToaster(false)}
        title="Item Deleted"
        message="The item has been removed from your list."
        type="success"
        action={{
          text: 'Undo',
          icon: 'undo',
          onClick: handleUndo,
        }}
      />

      <Toaster
        isOpen={linkToaster}
        onClose={() => setLinkToaster(false)}
        title="Report Ready"
        message="Your monthly report has been generated."
        type="success"
        link={{
          text: 'View Report',
          href: '/reports/monthly',
          icon: 'document',
        }}
      />

      <Section>
        <SectionTitle>Component Overview</SectionTitle>
        <SectionDescription>
          The Toaster component displays non-intrusive notifications that appear at the
          top of the screen and automatically dismiss after a set duration.
        </SectionDescription>

        <Card title="Features" headerBorder>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>Multiple Types:</strong> Success, error, and info variants with appropriate icons
            <br />
            • <strong>Auto-Dismiss:</strong> Configurable duration before automatic dismissal
            <br />
            • <strong>Actions:</strong> Optional action buttons (Undo, Retry, etc.)
            <br />
            • <strong>Links:</strong> Optional navigation links
            <br />
            • <strong>Portal Rendering:</strong> Uses React portals for proper z-index layering
            <br />
            • <strong>Theme Support:</strong> Adapts to light and dark themes
            <br />
            • <strong>Accessible:</strong> Screen reader friendly with appropriate ARIA labels
            <br />• <strong>Animations:</strong> Smooth slide-in and slide-out animations
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <CodeBlock>
{`import Toaster from 'components/Toaster';

const [toasterOpen, setToasterOpen] = useState(false);

// Success message
<Toaster
  isOpen={toasterOpen}
  onClose={() => setToasterOpen(false)}
  title="Success!"
  message="Your changes have been saved."
  type="success"
  duration={5000}
/>

// Error message
<Toaster
  isOpen={errorOpen}
  onClose={() => setErrorOpen(false)}
  title="Error"
  message="Failed to save changes. Please try again."
  type="error"
  duration={5000}
/>

// Info message
<Toaster
  isOpen={infoOpen}
  onClose={() => setInfoOpen(false)}
  title="New Feature"
  message="Check out our new dashboard!"
  type="info"
  duration={5000}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Actions</SectionTitle>
        <SectionDescription>
          Toasters can include action buttons for user interactions like undoing an action
          or retrying a failed operation.
        </SectionDescription>

        <CodeBlock>
{`// Toaster with undo action
<Toaster
  isOpen={deleteToasterOpen}
  onClose={() => setDeleteToasterOpen(false)}
  title="Customer Deleted"
  message="The customer has been removed from your list."
  type="success"
  action={{
    text: 'Undo',
    icon: 'undo',
    onClick: handleUndo,
  }}
/>

// Toaster with retry action
<Toaster
  isOpen={errorToasterOpen}
  onClose={() => setErrorToasterOpen(false)}
  title="Upload Failed"
  message="Failed to upload the file. Please try again."
  type="error"
  action={{
    text: 'Retry',
    icon: 'retry',
    onClick: handleRetry,
  }}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Links</SectionTitle>
        <SectionDescription>
          Toasters can include links to navigate to related pages or resources.
        </SectionDescription>

        <CodeBlock>
{`// Toaster with navigation link
<Toaster
  isOpen={reportToasterOpen}
  onClose={() => setReportToasterOpen(false)}
  title="Report Ready"
  message="Your monthly report has been generated."
  type="success"
  link={{
    text: 'View Report',
    href: '/reports/monthly',
    icon: 'document',
  }}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <Card headerBorder title="Toaster Props">
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>isOpen</strong> (boolean, required): Whether the toaster is visible
            <br />
            • <strong>onClose</strong> (function, required): Callback when toaster is closed
            <br />
            • <strong>title</strong> (string): Title text
            <br />
            • <strong>message</strong> (string): Message body text
            <br />
            • <strong>type</strong> (string): Type of toaster - "success", "error", or "info" (default: "success")
            <br />
            • <strong>duration</strong> (number): Auto-dismiss duration in milliseconds (default: 5000)
            <br />
            • <strong>action</strong> (object): Optional action button {'{text, icon, onClick}'}
            <br />
            • <strong>link</strong> (object): Optional navigation link {'{text, href, icon, onClick, target}'}
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Complete Examples</SectionTitle>
        <CodeBlock>
{`import React, { useState } from 'react';
import Toaster from 'components/Toaster';
import ButtonV2 from 'components/ButtonV2';

const ExampleComponent = () => {
  const [toasterOpen, setToasterOpen] = useState(false);
  const [toasterConfig, setToasterConfig] = useState({});

  const showSuccessToaster = () => {
    setToasterConfig({
      title: 'Success!',
      message: 'Your changes have been saved.',
      type: 'success',
    });
    setToasterOpen(true);
  };

  const showErrorToaster = () => {
    setToasterConfig({
      title: 'Error',
      message: 'Something went wrong. Please try again.',
      type: 'error',
      action: {
        text: 'Retry',
        icon: 'retry',
        onClick: handleRetry,
      },
    });
    setToasterOpen(true);
  };

  const showUndoToaster = () => {
    setToasterConfig({
      title: 'Item Deleted',
      message: 'The item has been removed.',
      type: 'success',
      action: {
        text: 'Undo',
        icon: 'undo',
        onClick: handleUndo,
      },
    });
    setToasterOpen(true);
  };

  return (
    <div>
      <ButtonV2 onClick={showSuccessToaster}>
        Show Success
      </ButtonV2>
      <ButtonV2 onClick={showErrorToaster}>
        Show Error
      </ButtonV2>
      <ButtonV2 onClick={showUndoToaster}>
        Delete with Undo
      </ButtonV2>

      <Toaster
        isOpen={toasterOpen}
        onClose={() => setToasterOpen(false)}
        {...toasterConfig}
      />
    </div>
  );
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Toaster Helper Pattern</SectionTitle>
        <SectionDescription>
          A common pattern is to create a global toaster context or hook for showing toasters
          from anywhere in the app.
        </SectionDescription>

        <CodeBlock>
{`// toasterContext.js
import React, { createContext, useContext, useState } from 'react';
import Toaster from 'components/Toaster';

const ToasterContext = createContext();

export const useToaster = () => useContext(ToasterContext);

export const ToasterProvider = ({ children }) => {
  const [toaster, setToaster] = useState({ isOpen: false });

  const showToaster = (config) => {
    setToaster({ isOpen: true, ...config });
  };

  const closeToaster = () => {
    setToaster({ isOpen: false });
  };

  return (
    <ToasterContext.Provider value={{ showToaster, closeToaster }}>
      {children}
      <Toaster
        {...toaster}
        onClose={closeToaster}
      />
    </ToasterContext.Provider>
  );
};

// Usage in any component
import { useToaster } from 'contexts/toasterContext';

const MyComponent = () => {
  const { showToaster } = useToaster();

  const handleSave = async () => {
    try {
      await saveData();
      showToaster({
        title: 'Saved!',
        message: 'Your changes have been saved.',
        type: 'success',
      });
    } catch (error) {
      showToaster({
        title: 'Error',
        message: 'Failed to save changes.',
        type: 'error',
      });
    }
  };

  return <button onClick={handleSave}>Save</button>;
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Use success type for completed actions
              <br />
              • Use error type for failed operations
              <br />
              • Provide clear, concise messages
              <br />
              • Include actions when users might want to undo or retry
              <br />
              • Set appropriate duration based on message length
              <br />• Close toasters programmatically after action completion
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't use toasters for critical errors (use modals)
              <br />
              • Don't show multiple toasters at once
              <br />
              • Don't use very short durations (less than 3 seconds)
              <br />
              • Don't put long messages in toasters
              <br />• Don't use toasters for form validation errors
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Common Use Cases</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Success Toasters:</strong>
            <br />• Changes saved successfully
            <br />• Item created/updated/deleted
            <br />• Message sent successfully
            <br />• File uploaded successfully
            <br />
            <br />
            <strong>Error Toasters:</strong>
            <br />• Network request failed
            <br />• Upload failed
            <br />• Invalid data format
            <br />• Permission denied
            <br />
            <br />
            <strong>Info Toasters:</strong>
            <br />• New features available
            <br />• System maintenance scheduled
            <br />• Updates in progress
            <br />• Background tasks completed
          </div>
        </Card>
      </Section>
    </div>
  );
};

export default ToasterDocs;